import { useBilling } from '@hook'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAppSelector } from '@store/hooks'
import { CheckBox, FormButton, FormInputField, Modal } from '@ui'
import { FormSelect } from '@ui/SearchInput/Select'
import { getError, isAdmissionTab, isDischargeTab, isEncounterTab, isRoundingTab, showToast } from '@util'
import { Box, Text } from 'grommet'
import { useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import useFormPersist from 'react-hook-form-persist'
import { useParams, useSearchParams } from 'react-router-dom'
import { boolean, InferType, object, string } from 'yup'

type Props = {
  onClose: () => void
}

export const invoiceSchema = object({
  bill_to_hmo: boolean(),
  category: string().required('category is required'),
  description: string(),
  name: string().required('name is required'),
  quantity: string().required('quantity is required'),
  unit_price: string().required('price is required'),
  source_id: string(),
  source_type: string()
})

export type InvoicePayload = InferType<typeof invoiceSchema>

export const AddItem = ({ onClose }: Props) => {
  const { id } = useParams()
  const { encounterId } = useParams()
  const [searchParams] = useSearchParams()
  const encounterTab = isEncounterTab(searchParams)
  const stateCategory = useAppSelector(state => state.config.invoice_item_category)

  const { billing, unbilledItems } = useBilling({ fetchList: true, unbilledId: id })
  const invoiceId = billing?.filter(invoice => invoice?.relationships?.encounter?.data?.id === encounterId)[0]?.id
  const { createInvoice } = useBilling(encounterTab ? { id: invoiceId } : { id })

  const unbilledOptions = useMemo(() => {
    return Object.keys(unbilledItems || {}).map(key => ({
      name: key.replace(/_/g, ' '),
      value: key
    }))
  }, [unbilledItems])

  const methods = useForm({ resolver: yupResolver(invoiceSchema) })
  const {
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { isSubmitting }
  } = methods

  const categoryValue = watch('category')
  const nameValue = watch('name')

  const nameOptions = useMemo(() => {
    if (!categoryValue || !unbilledItems) return []
    return (unbilledItems[categoryValue] || []).map(item => ({
      ...item,
      label: item.name,
      value: item.name
    }))
  }, [categoryValue, unbilledItems])

  const priceOptions = useMemo(() => {
    if (!categoryValue || !nameValue || !unbilledItems) return []
    const selectedItem = unbilledItems[categoryValue]?.find(item => item.name === nameValue)
    if (!selectedItem) return []

    const options = [
      {
        label: `Hospital Price - NGN ${selectedItem.hospital_price.toLocaleString()}`,
        value: selectedItem.hospital_price.toString()
      }
    ]

    if (selectedItem.hmo_price) {
      options.push({
        label: `HMO Price - NGN ${selectedItem.hmo_price.toLocaleString()}`,
        value: selectedItem.hmo_price.toString()
      })
    }

    return options
  }, [categoryValue, nameValue, unbilledItems])

  useEffect(() => {
    if (categoryValue && nameValue && unbilledItems) {
      const selectedItem = unbilledItems[categoryValue]?.find(item => item.name === nameValue)
      if (selectedItem) {
        setValue('unit_price', selectedItem.hospital_price.toString())
        setValue('bill_to_hmo', !!selectedItem.bill_to_hmo)
        setValue('quantity', '1')
        setValue('source_id', selectedItem.id.toString())
        setValue('source_type', selectedItem.source_type)
        if (selectedItem.description) {
          setValue('description', selectedItem.description)
        }
      }
    }
  }, [categoryValue, nameValue, unbilledItems, setValue])

  useEffect(() => {
    if (categoryValue) {
      const currentName = watch('name')
      const itemsInCategory = unbilledItems?.[categoryValue] || []
      const nameExistsInNewCategory = itemsInCategory.some(item => item.name === currentName)

      if (!nameExistsInNewCategory) {
        setValue('name', '')
        setValue('unit_price', '')
        setValue('quantity', '')
        setValue('source_id', '')
        setValue('source_type', '')
        setValue('bill_to_hmo', false)
      }
    }
  }, [categoryValue, setValue, unbilledItems])

  const { clear } = useFormPersist('addItem', { setValue, storage: window.localStorage, watch })

  const handleClose = () => {
    clear()
    reset()
    onClose()
  }

  const getCreatedFrom = () => {
    if (isAdmissionTab(searchParams)) return 'admissions'
    if (isDischargeTab(searchParams)) return 'discharges'
    if (isRoundingTab(searchParams)) return 'roundings'
    return 'general'
  }

  const onSubmit = async (formVal: InvoicePayload) => {
    const { source_id, source_type, ...rest } = formVal
    const created_from = getCreatedFrom()
    const payload = {
      data: {
        attributes: {
          ...rest,
          created_from: created_from,
          source_id: source_id,
          source_type: source_type
        },
        type: 'invoice_item'
      }
    }

    try {
      await createInvoice(payload)
      showToast('Invoice item created successfully')
      handleClose()
    } catch (error) {
      getError(error)
    }
  }
  return (
    <FormProvider {...methods}>
      <Modal onClose={onClose} title="Add item">
        <form
          className="flex flex-col gap-4 w-full min-w-0 max-w-[530px] overflow-auto"
          onSubmit={handleSubmit(onSubmit)}
        >
          <div className="mx-auto flex flex-col gap-4 w-full min-w-0 max-w-[399px] py-6 overflow-auto">
            <div>
              <FormSelect
                getOptionLabel={({ name }) => name}
                getOptionValue={({ value }) => value}
                label="Category"
                name="category"
                options={unbilledOptions}
              />
            </div>
            <FormSelect
              getOptionLabel={({ label }) => label}
              getOptionValue={({ value }) => value}
              label="Name"
              name="name"
              options={nameOptions}
              placeholder="E.g Paracetamol"
            />
            <FormSelect
              getOptionLabel={({ label }) => label}
              getOptionValue={({ value }) => value}
              isCreatable
              label="Unit pricing"
              name="unit_price"
              options={priceOptions}
              placeholder="E.g 5000"
            />
            <FormInputField
              label={categoryValue === 'rooms' ? 'Days' : 'Quantity/Duration'}
              name="quantity"
              placeholder=""
              type="number"
            />
            <Controller
              control={control}
              name="bill_to_hmo"
              render={({ field: { onChange, value } }) => (
                <div className="flex gap-1 items-center">
                  <CheckBox
                    checked={value}
                    handleChange={arg => {
                      onChange(arg)
                    }}
                  />
                  <Text size="14px">Bill to HMO</Text>
                </div>
              )}
            />
          </div>
          <FormButton isLoading={isSubmitting} onClose={handleClose} />
        </form>
      </Modal>
    </FormProvider>
  )
}
